import { useState, useMemo, useCallback } from 'react';

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 日付範囲のプリセット種別を定義する
 * @graph-connects none
 */
export type DatePreset = '7d' | '30d' | '90d' | 'custom';

/** @graph-connects none */
function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** @graph-connects none */
function getDateRange(preset: Exclude<DatePreset, 'custom'>): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const end = formatDate(now);
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { startDate: formatDate(start), endDate: end };
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 日付範囲管理フック。7d/30d/90dプリセットとカスタム日付範囲の状態管理
 * @graph-connects none
 */
export function useDateRange(defaultPreset: Exclude<DatePreset, 'custom'> = '30d') {
  const [preset, setPresetState] = useState<DatePreset>(defaultPreset);
  const [customRange, setCustomRange] = useState<{ startDate: string; endDate: string } | null>(
    null,
  );

  const { startDate, endDate } = useMemo(() => {
    if (preset === 'custom' && customRange) {
      return customRange;
    }
    return getDateRange(preset === 'custom' ? defaultPreset : preset);
  }, [preset, customRange, defaultPreset]);

  const setPreset = useCallback((p: DatePreset) => {
    setPresetState(p);
    if (p !== 'custom') {
      setCustomRange(null);
    }
  }, []);

  const setCustomDates = useCallback((start: string, end: string) => {
    setPresetState('custom');
    setCustomRange({ startDate: start, endDate: end });
  }, []);

  return { preset, setPreset, startDate, endDate, setCustomDates };
}
